import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useI18n } from "@/contexts/I18nContext";
import { useListCategories, useListListings, useListMerchants, useGetStats } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Store, ArrowRight, ShieldAlert, CheckCircle2, Lock, Tag } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { ListingImage } from "@/components/listing-image";

type SearchSuggestion = {
  text: string;
  type: "listing" | "merchant" | "category";
};

function normalizeSearchText(value: string) {
  return value.toLocaleLowerCase().replace(/\s+/g, " ").trim();
}

export default function HomePage() {
  const { t, isRTL } = useI18n();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [area, setArea] = useState<string>("all");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSuggestionMenuOpen, setIsSuggestionMenuOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  const { data: categories } = useListCategories();
  const { data: featuredListings } = useListListings({ featured: true, limit: 6 });
  const { data: featuredMerchants } = useListMerchants({ featured: true, limit: 3 });
  const { data: stats } = useGetStats();
  const canSearchSuggestions = debouncedSearch.length >= 2;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: suggestionResults } = useListListings(
    { q: debouncedSearch, limit: 10 },
    { query: { enabled: canSearchSuggestions, retry: false } as any },
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(normalizeSearchText(search));
    }, 275);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        setIsSuggestionMenuOpen(false);
        setActiveSuggestionIndex(-1);
      }
    };
    document.addEventListener("pointerdown", closeOnOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointerDown);
  }, []);

  const suggestions = useMemo<SearchSuggestion[]>(() => {
    if (!canSearchSuggestions) return [];

    const query = normalizeSearchText(debouncedSearch);
    const allSuggestions: SearchSuggestion[] = [];
    for (const listing of suggestionResults?.listings ?? []) {
      allSuggestions.push({ text: listing.title, type: "listing" });
      allSuggestions.push({ text: listing.merchantName, type: "merchant" });
      allSuggestions.push({ text: listing.categoryName, type: "category" });
    }
    for (const category of categories ?? []) {
      allSuggestions.push({ text: category.name, type: "category" });
    }

    const uniqueSuggestions = new Map<string, SearchSuggestion>();
    for (const suggestion of allSuggestions) {
      const normalized = normalizeSearchText(suggestion.text);
      if (normalized.includes(query) && !uniqueSuggestions.has(normalized)) {
        uniqueSuggestions.set(normalized, suggestion);
      }
    }

    return [...uniqueSuggestions.values()]
      .sort((first, second) => {
        const firstPrefix = normalizeSearchText(first.text).startsWith(query);
        const secondPrefix = normalizeSearchText(second.text).startsWith(query);
        if (firstPrefix !== secondPrefix) return firstPrefix ? -1 : 1;
        return first.text.localeCompare(second.text);
      })
      .slice(0, 5);
  }, [canSearchSuggestions, categories, debouncedSearch, suggestionResults]);

  useEffect(() => {
    setActiveSuggestionIndex(-1);
  }, [suggestions]);

  const navigateToSearch = (query = search) => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (area && area !== "all") params.set("area", area);
    setLocation(`/explore?${params.toString()}`);
    setIsSuggestionMenuOpen(false);
    setActiveSuggestionIndex(-1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigateToSearch();
  };

  const selectSuggestion = (suggestion: SearchSuggestion) => {
    setSearch(suggestion.text);
    navigateToSearch(suggestion.text);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsSuggestionMenuOpen(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    if (!suggestions.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsSuggestionMenuOpen(true);
      setActiveSuggestionIndex((current) => (current + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsSuggestionMenuOpen(true);
      setActiveSuggestionIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
    } else if (event.key === "Enter" && isSuggestionMenuOpen && activeSuggestionIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeSuggestionIndex]);
    }
  };

  const DISCLAIMER = t("Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.");

  return (
    <div className="flex-1 w-full bg-background flex flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[75vh] flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-[#062A60] via-[#0b3a4d] to-[#08745c] text-white pt-24 pb-32 px-4">
        <div className="absolute inset-0 z-0 bg-[url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2940&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#062A60]/80 via-transparent to-transparent z-0"></div>
        
        {/* Animated emerald rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
          <div className="absolute w-[800px] h-[800px] rounded-full border border-primary/20 bg-primary/5 animate-[spin_60s_linear_infinite] mix-blend-overlay opacity-50"></div>
          <div className="absolute w-[600px] h-[600px] rounded-full border border-primary/30 bg-primary/10 animate-[spin_40s_linear_infinite_reverse] mix-blend-overlay opacity-50"></div>
          <div className="absolute w-[400px] h-[400px] rounded-full border border-primary/40 bg-primary/20 blur-3xl opacity-30"></div>
        </div>

        <div className="container relative z-10 max-w-4xl mx-auto flex flex-col items-center text-center space-y-8 animate-in fade-in zoom-in duration-700">
          <Badge variant="outline" className="bg-primary/20 text-white border-primary/30 px-5 py-2 rounded-full text-sm font-semibold tracking-wide shadow-[0_0_15px_rgba(16,185,129,0.3)] backdrop-blur-md">
            {t("Discover USDT-Friendly Businesses in Dubai")}
          </Badge>
          <div className="flex items-center gap-3 text-sm font-bold tracking-[0.18em] uppercase text-white/80">
            <img src="/brand/souq-path-concept.svg" alt="" className="h-10 w-10 rounded-xl shadow-lg border border-white/10" />
            <span>{t("Souq means market")}</span>
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-balance leading-tight drop-shadow-lg">
            {t("Discover where to spend USDT in Dubai")}
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl font-medium leading-relaxed drop-shadow-md">
            {t("Browse products and services from independent Dubai merchants that state they accept USDT. Contact each merchant directly to confirm availability, pricing, and payment terms.")}
          </p>

          <form onSubmit={handleSearch} className="w-full max-w-3xl flex flex-col sm:flex-row gap-2 bg-card/95 backdrop-blur-xl p-3 rounded-3xl shadow-2xl shadow-black/30 border border-white/20">
            <div ref={searchBoxRef} className="relative flex-1 flex items-center">
              <Search className={`absolute h-6 w-6 text-muted-foreground ${isRTL ? "right-5" : "left-5"}`} />
              <Input
                type="text"
                placeholder={t("Search products, services, or businesses")}
                className={`h-16 bg-transparent border-none shadow-none focus-visible:ring-0 text-card-foreground text-lg placeholder:text-muted-foreground/70 ${isRTL ? "pr-14" : "pl-14"}`}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setIsSuggestionMenuOpen(e.target.value.trim().length >= 2);
                }}
                onFocus={() => setIsSuggestionMenuOpen(search.trim().length >= 2)}
                onKeyDown={handleSearchKeyDown}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={isSuggestionMenuOpen && suggestions.length > 0}
                aria-controls="hero-search-suggestions"
                aria-activedescendant={activeSuggestionIndex >= 0 ? `hero-search-suggestion-${activeSuggestionIndex}` : undefined}
                data-testid="input-hero-search"
              />
              {isSuggestionMenuOpen && suggestions.length > 0 && (
                <ul
                  id="hero-search-suggestions"
                  role="listbox"
                  className={`absolute top-full z-30 mt-4 max-h-72 w-full overflow-y-auto rounded-2xl border border-border/60 bg-card/95 backdrop-blur-xl p-2 text-foreground shadow-2xl ${isRTL ? "right-0 text-right" : "left-0 text-left"}`}
                  data-testid="hero-search-suggestions"
                >
                  {suggestions.map((suggestion, index) => (
                    <li key={`${suggestion.type}-${normalizeSearchText(suggestion.text)}`} role="option" aria-selected={index === activeSuggestionIndex}>
                      <button
                        type="button"
                        className={`flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-base font-medium transition-colors hover:bg-primary/10 hover:text-primary focus:bg-primary/10 focus:outline-none ${index === activeSuggestionIndex ? "bg-primary/10 text-primary" : ""}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => selectSuggestion(suggestion)}
                        data-testid={`hero-search-suggestion-${index}`}
                      >
                        {suggestion.type === "merchant" ? <Store className="h-5 w-5 shrink-0 text-primary" /> : suggestion.type === "category" ? <Tag className="h-5 w-5 shrink-0 text-primary" /> : <Search className="h-5 w-5 shrink-0 text-primary" />}
                        <span className="min-w-0 flex-1 truncate">{suggestion.text}</span>
                        <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(suggestion.type === "listing" ? "Listing" : suggestion.type === "merchant" ? "Merchant" : "Category")}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="w-full sm:w-56 border-t sm:border-t-0 sm:border-l border-border/50 pt-2 sm:pt-0 sm:pl-2">
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger className="w-full h-16 bg-transparent border-none shadow-none focus:ring-0 text-card-foreground font-semibold text-lg" data-testid="select-hero-area">
                  <SelectValue placeholder={t("All Areas")} />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-border/60 shadow-xl">
                  <SelectItem value="all" className="rounded-lg font-medium">{t("All Areas")}</SelectItem>
                  {stats?.areas?.map(a => (
                    <SelectItem key={a} value={a} className="rounded-lg font-medium">{t(a)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" size="lg" className="h-16 px-10 rounded-2xl font-bold text-lg shadow-lg hover:shadow-primary/25 transition-all hover:-translate-y-0.5" data-testid="btn-hero-search">
              {t("Explore")}
            </Button>
          </form>

          <div className="flex flex-wrap items-center justify-center gap-6 pt-6">
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 drop-shadow-sm">
              <CheckCircle2 className="h-5 w-5 text-primary" /> <span>{t("Direct Peer-to-Peer")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 drop-shadow-sm">
              <CheckCircle2 className="h-5 w-5 text-primary" /> <span>{t("No Wallets Connected")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 drop-shadow-sm">
              <CheckCircle2 className="h-5 w-5 text-primary" /> <span>{t("Independent Merchants")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 px-4 container mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="text-4xl font-extrabold tracking-tight text-[#062A60] dark:text-white mb-3">{t("Categories")}</h2>
            <p className="text-lg text-muted-foreground">{t("Browse USDT-friendly businesses by category.")}</p>
          </div>
          <Button variant="ghost" asChild className="font-bold text-primary hover:text-primary/80 hover:bg-primary/5 rounded-full px-6">
            <Link href="/categories" data-testid="link-view-all-categories">
              {t("View all")} <ArrowRight className={`h-5 w-5 ${isRTL ? "rtl-flip mr-2" : "ml-2"}`} />
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {categories?.slice(0, 8).map((category, i) => {
            const Icon = getCategoryIcon(category.name);
            return (
              <Link key={category.id} href={`/explore?categoryId=${category.id}`} data-testid={`card-category-${category.id}`}>
                <Card className="rounded-3xl border-border/60 bg-card hover:border-primary/50 hover:shadow-xl transition-all duration-300 cursor-pointer group animate-in fade-in slide-in-from-bottom-4 hover:-translate-y-1" style={{ animationDelay: `${i * 50}ms` }}>
                  <CardContent className="p-8 flex flex-col items-center justify-center text-center gap-5 h-full">
                    <div className="h-16 w-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 shadow-sm ring-1 ring-primary/20">
                      <Icon className="h-8 w-8" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl group-hover:text-primary transition-colors">{t(category.name)}</h3>
                      <Badge variant="secondary" className="mt-3 bg-muted border-border/50 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors shadow-sm font-medium">
                        {category.listingCount} {t("listings")}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured Listings */}
      <section className="py-24 px-4 bg-muted/10 border-y border-border/50">
        <div className="container mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <Badge variant="outline" className="mb-3 border-accent text-accent bg-accent/5 px-4 py-1.5 text-sm font-bold uppercase tracking-wider">{t("Featured")}</Badge>
              <h2 className="text-4xl font-extrabold tracking-tight text-[#062A60] dark:text-white">{t("Featured Listings")}</h2>
            </div>
            <Button variant="outline" asChild className="hidden sm:flex rounded-full px-6 font-bold shadow-sm hover:shadow-md transition-all border-border/60" data-testid="link-browse-featured">
              <Link href="/explore?featured=true">{t("Browse all featured")}</Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredListings?.listings?.map((listing) => (
              <Link key={listing.id} href={`/listings/${listing.slug ?? listing.id}`} className="group h-full block" data-testid={`card-listing-${listing.id}`}>
                <Card className="h-full overflow-hidden rounded-3xl border-border/60 hover:border-primary/50 hover:shadow-xl transition-all duration-300 flex flex-col hover:-translate-y-1">
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted w-full">
                    <ListingImage src={listing.imageUrl} alt={listing.title} imgClassName="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
                      <Badge className="souq-usdt-badge bg-primary/95 hover:bg-primary text-primary-foreground backdrop-blur-sm shadow-sm font-bold px-3 py-1 text-sm">
                        {t("USDT Accepted")}
                      </Badge>
                      <Badge variant="outline" className="bg-accent/90 border-accent-foreground/10 text-accent-foreground backdrop-blur-sm shadow-sm font-bold px-3 py-1 text-sm">
                        {t("Premium Only")}
                      </Badge>
                    </div>
                    <div className="absolute bottom-4 right-4">
                      <Badge variant="secondary" className="souq-category-badge backdrop-blur-md font-bold shadow-md px-3 py-1.5 text-sm">
                        {listing.categoryName}
                      </Badge>
                    </div>
                    {listing.isDemo && (
                      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-xs py-1.5 px-4 text-center backdrop-blur-md font-medium uppercase tracking-wider">
                        {t("Demo")}
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="mb-3">
                      <h3 className="font-bold text-2xl line-clamp-2 group-hover:text-primary transition-colors leading-tight">{listing.title}</h3>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 font-semibold">
                      <Store className="h-4 w-4 text-foreground/50" /> 
                      <span className="truncate">{listing.merchantName}</span>
                      <span className="text-border mx-1">•</span>
                      <MapPin className="h-4 w-4 text-foreground/50" /> 
                      <span className="truncate">{listing.area}</span>
                    </div>
                    <p className="text-muted-foreground text-base line-clamp-2 mb-6 flex-1 leading-relaxed">
                      {listing.description}
                    </p>
                    <div className="pt-5 border-t border-border/50 flex items-center justify-between mt-auto">
                      <div className="font-extrabold text-xl text-[#062A60] dark:text-white">
                        {listing.priceAed ? `AED ${listing.priceAed.toLocaleString()}` : t("Contact for price")}
                      </div>
                      <Button variant="ghost" size="sm" className="font-bold rounded-full group-hover:bg-primary group-hover:text-primary-foreground transition-all" data-testid={`btn-view-listing-${listing.id}`}>
                        {t("View Details")} <ArrowRight className={`h-4 w-4 ${isRTL ? "rtl-flip mr-2" : "ml-2"}`} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="mt-10 text-center sm:hidden">
            <Button variant="outline" size="lg" asChild className="rounded-full px-8 font-bold shadow-sm w-full">
              <Link href="/explore?featured=true">{t("Browse all featured")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-32 px-4 container mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-[#062A60] dark:text-white">{t("How It Works")}</h2>
          <p className="text-xl text-muted-foreground leading-relaxed">{t("Simple steps to find USDT-friendly businesses in Dubai.")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-12 relative max-w-5xl mx-auto">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-border via-primary/40 to-border -translate-y-1/2 z-0"></div>

          <div className="relative z-10 flex flex-col items-center text-center space-y-6">
            <div className="w-24 h-24 rounded-3xl bg-card border-2 border-border/60 shadow-xl shadow-black/5 flex items-center justify-center text-4xl font-extrabold text-primary mb-2 ring-4 ring-background">1</div>
            <h3 className="text-2xl font-bold text-[#062A60] dark:text-white">{t("Discover")}</h3>
            <p className="text-lg text-muted-foreground leading-relaxed">{t("Find listed products and services from Dubai merchants.")}</p>
          </div>
          <div className="relative z-10 flex flex-col items-center text-center space-y-6">
            <div className="w-24 h-24 rounded-3xl bg-card border-2 border-border/60 shadow-xl shadow-black/5 flex items-center justify-center text-4xl font-extrabold text-primary mb-2 ring-4 ring-background">2</div>
            <h3 className="text-2xl font-bold text-[#062A60] dark:text-white">{t("Contact the Merchant")}</h3>
            <p className="text-lg text-muted-foreground leading-relaxed">{t("Contact directly via WhatsApp, phone, or email to confirm details and availability.")}</p>
          </div>
          <div className="relative z-10 flex flex-col items-center text-center space-y-6">
            <div className="w-24 h-24 rounded-3xl bg-card border-2 border-border/60 shadow-xl shadow-black/5 flex items-center justify-center text-4xl font-extrabold text-primary mb-2 ring-4 ring-background">3</div>
            <h3 className="text-2xl font-bold text-[#062A60] dark:text-white">{t("Arrange Payment Directly")}</h3>
            <p className="text-lg text-muted-foreground leading-relaxed">{t("Agree with the merchant on price, transfer network, and payment terms. Souq Crypto does not receive any funds.")}</p>
          </div>
        </div>
      </section>

      {/* Regional Expansion Teaser */}
      <section className="py-24 px-4 bg-muted/10 border-y border-border/50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-6 border-primary/40 text-primary bg-primary/10 tracking-widest text-sm font-bold uppercase px-5 py-2 shadow-sm rounded-full">
              {t("OUR EXPANSION ROADMAP")}
            </Badge>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-[#062A60] dark:text-white">
              {t("Dubai First. Three Major Cities Next.")}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {t("Dubai is our first-year launch market. As our merchant community grows, Souq Crypto plans to expand into three major regional cities. The next destinations will be revealed as partnerships are confirmed.")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Dubai — active */}
            <Card className="relative overflow-hidden border-primary/30 bg-card shadow-xl shadow-primary/5 rounded-3xl">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40" />
              <CardContent className="p-8 flex flex-col items-center text-center gap-4">
                <div className="h-20 w-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center ring-1 ring-primary/20 shadow-sm">
                  <MapPin className="h-10 w-10" />
                </div>
                <div>
                  <Badge className="bg-primary text-primary-foreground text-xs mb-3 font-bold uppercase tracking-wider px-3 py-1">
                    {t("YEAR 1 — LAUNCH MARKET")}
                  </Badge>
                  <h3 className="text-2xl font-extrabold text-[#062A60] dark:text-white">{t("Dubai")}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                  {t("Building Dubai's focused directory for discovering USDT-friendly products, services, and businesses.")}
                </p>
              </CardContent>
            </Card>

            {/* Three locked upcoming-city cards */}
            {([1, 2, 3] as const).map((i) => (
              <Card key={i} className="relative overflow-hidden border-border/40 opacity-70 hover:opacity-100 transition-opacity duration-300 rounded-3xl bg-card">
                <CardContent className="p-8 flex flex-col items-center text-center gap-4">
                  <div className="h-20 w-20 rounded-2xl bg-muted text-muted-foreground flex items-center justify-center ring-1 ring-border/50">
                    <Lock className="h-8 w-8 opacity-50" />
                  </div>
                  <div>
                    <Badge variant="outline" className="text-xs mb-3 border-border text-muted-foreground font-bold uppercase tracking-wider px-3 py-1">
                      {t("TO BE REVEALED")}
                    </Badge>
                    <h3 className="text-2xl font-bold text-muted-foreground">{t("NEXT CITY")}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                    {t("A major regional market is planned as part of the next expansion phase.")}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-base font-medium text-muted-foreground/80 mt-12 italic">
            {t("Follow our journey. The next cities will be announced soon.")}
          </p>
        </div>
      </section>

      {/* Merchant CTA Section */}
      <section className="py-32 px-4 bg-gradient-to-br from-[#062A60] to-[#0b3a4d] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/3 pointer-events-none"></div>
        
        <div className="container mx-auto relative z-10 text-center max-w-4xl">
          <div className="inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 text-accent mb-8 shadow-2xl">
            <ShieldAlert className="h-12 w-12" />
          </div>
          <h2 className="text-4xl md:text-6xl font-extrabold mb-8 tracking-tight drop-shadow-md">{t("Are you a Dubai merchant?")}</h2>
          <p className="text-xl md:text-2xl text-white/80 mb-12 leading-relaxed font-medium">
            {t("List your Dubai business on Souq Crypto and connect with customers searching for USDT-friendly merchants.")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button size="lg" className="h-16 px-10 w-full sm:w-auto text-xl font-bold rounded-2xl shadow-xl shadow-primary/20 hover:shadow-primary/40 transition-all hover:-translate-y-1" asChild data-testid="btn-list-business-cta">
              <Link href="/list-your-business">{t("List Your Business Now")}</Link>
            </Button>
            <Button size="lg" variant="outline" className="h-16 px-10 w-full sm:w-auto text-xl font-bold rounded-2xl border-white/20 bg-white/5 hover:bg-white/10 text-white backdrop-blur-sm transition-all hover:-translate-y-1" asChild>
              <Link href="/for-businesses">{t("Learn More")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-10 px-4 bg-background border-t border-border/40">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-start gap-4 text-sm text-muted-foreground font-medium bg-muted/30 p-6 rounded-2xl border border-border/50">
            <ShieldAlert className="h-6 w-6 shrink-0 text-accent mt-0.5" />
            <p className="leading-relaxed">{DISCLAIMER}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
